<script>
  import { onMount } from 'svelte';
  import PopupMap from "./PopupMap.svelte";

  export let tour;
  export let map;

  let selectedIndex = -1;
  let items = [];
  let panelElement;
  let filterText = '';
  let searchInput;

  // Combine POIs and buildings into a single accessible list
  $: {
    items = [];
    
    // Add POIs if they exist
    if (tour?.mapbox?.pois) {
      items.push(...tour.mapbox.pois.filter(poi => !poi.hidden).map(poi => ({
        type: 'poi',
        title: poi.title,
        description: poi.description,
        data: poi
      })));
    }

    // Add numeric POIs if they exist
    if (tour?.mapbox?.numericPois) {
      items.push(...tour.mapbox.numericPois.filter(poi => !poi.hidden).map((poi, index) => ({
        type: 'numericPoi',
        title: `${index + 1}. ${poi.title}`,
        description: poi.description,
        data: poi
      })));
    }

    // Add buildings if they exist
    if (tour?.mapbox?.buildings) {
      items.push(...tour.mapbox.buildings.filter(building => !building.hidden).map(building => ({
        type: 'building',
        title: building.title,
        description: building.popupText,
        data: building
      })));
    }

    // Add polygon POIs if they exist
    if (tour?.mapbox?.polygonPois) {
      items.push(...tour.mapbox.polygonPois.filter(poi => !poi.hidden).map(poi => ({
        type: 'polygonPoi',
        title: poi.title,
        description: poi.description,
        data: poi
      })));
    }
  }

  // Group items by type
  $: groupedItems = {
    pois: items.filter(item => item.type === 'poi'),
    numericPois: items.filter(item => item.type === 'numericPoi'),
    buildings: items.filter(item => item.type === 'building'),
    polygonPois: items.filter(item => item.type === 'polygonPoi')
  };

  // Add filtered items reactive declaration
  $: filteredGroupedItems = {
    pois: groupedItems.pois.filter(item => 
      item.title.toLowerCase().includes(filterText.toLowerCase())
    ),
    numericPois: groupedItems.numericPois.filter(item => 
      item.title.toLowerCase().includes(filterText.toLowerCase())
    ),
    buildings: groupedItems.buildings.filter(item => 
      item.title.toLowerCase().includes(filterText.toLowerCase())
    ),
    polygonPois: groupedItems.polygonPois.filter(item => 
      item.title.toLowerCase().includes(filterText.toLowerCase())
    )
  };

  // Update flattened items to use filtered groups (buildings first)
  $: filteredFlattenedItems = [
    ...filteredGroupedItems.buildings,
    ...filteredGroupedItems.pois,
    ...filteredGroupedItems.numericPois,
    ...filteredGroupedItems.polygonPois
  ];

  // When filter text changes, reset selection
  $: {
    filterText;
    selectedIndex = -1;
  }

  function focusItem(index) {
    selectedIndex = index;
    const buttons = panelElement?.querySelectorAll('.item-button');
    if (buttons?.[index]) {
      buttons[index].focus();
    }
  }

  function handleKeydown(event) {
    const popup = document.querySelector('.popup');
    if (event.key === 'Escape') {
      if (popup) {
        setTimeout(() => {
          searchInput?.focus();
        }, 0);
      }
    }
  }

  /** Zoom map to the item's location (building, POI, or polygon center). */
  function zoomToItem(item) {
    if (item.type === 'building') {
      const featureId = item.data.featureId;
      if (featureId && map) {
        const features = map.querySourceFeatures('base-buildings', {
          sourceLayer: tour?.mapbox?.mapboxTilesetLayerName,
          filter: ['==', ['get', '@id'], featureId]
        });
        if (features.length > 0) {
          const coordinates = features[0].geometry.coordinates[0];
          const center = coordinates.reduce((acc, coord) => {
            return [acc[0] + coord[0], acc[1] + coord[1]];
          }, [0, 0]).map(sum => sum / coordinates.length);
          map.flyTo({ center: center, zoom: 18 });
        }
      }
    } else if (item.type === 'polygonPoi' && item.data.coordinates?.length >= 3) {
      const coords = item.data.coordinates;
      let sumLng = 0, sumLat = 0, count = 0;
      coords.forEach(coord => {
        sumLng += coord[0];
        sumLat += coord[1];
        count++;
      });
      if (count > 0) {
        map.flyTo({
          center: [sumLng / count, sumLat / count],
          zoom: 16,
        });
      }
    } else if (item.data.location && map) {
      map.flyTo({
        center: item.data.location,
        zoom: 18,
      });
    }
  }

  /** Open popup for the item (current accessibility behavior). */
  function openPopupForItem(item) {
    const popup = {
      title: item.title,
      content: item.description,
      linkedscenes: item.data.sceneIds?.map(sceneId => {
        const scene = tour?.scenes?.find(scene => scene.id === sceneId);
        return {
          id: sceneId,
          name: scene?.name,
          title: scene?.title
        };
      }),
      directions: item.data.location
    };
    if (popup.title || popup.content || popup.linkedscenes?.length) {
      new PopupMap({
        target: document.body,
        props: popup
      });
    }
  }

  /**
   * Click: zoom to location only.
   * Keyboard (Enter/Space): open popup (accessibility behavior).
   * event.detail === 0 means activation from keyboard.
   */
  function handleItemSelect(item, event) {
    const isKeyboard = event && event.detail === 0;
    if (isKeyboard) {
      openPopupForItem(item);
    } else {
      zoomToItem(item);
    }
  }

  function handleItemFocus(item) {
    zoomToItem(item);
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  });
</script>

<div 
  class="mapmenu-panel"
  role="region"
  aria-label="Map menu"
  bind:this={panelElement}
>
  <div class="header">
    <h2 id="mapmenu-heading">Map Locations</h2>
    <div class="search">
      <input
        type="text"
        placeholder="Type to filter locations..."
        bind:value={filterText}
        bind:this={searchInput}
        aria-label="Filter locations"
      >
    </div>
  </div>
  <div 
    class="content"
    role="application"
    aria-labelledby="mapmenu-heading"
  >
    {#if filteredGroupedItems.buildings.length > 0}
      <div 
        class="group"
        role="region"
        aria-labelledby="building-heading"
      >
        <h3 id="building-heading" class="group-heading">Buildings</h3>
        <div role="listbox" aria-labelledby="building-heading">
          {#each filteredGroupedItems.buildings as item, index}
            <button
              class="item-button"
              class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              on:click={(e) => handleItemSelect(item, e)}
              on:focus={() => handleItemFocus(item)}
              aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              role="option"
            >
              {item.title}
            </button>
          {/each}
        </div>
      </div>
    {/if}

    {#if filteredGroupedItems.pois.length > 0}
      <div 
        class="group"
        role="region"
        aria-labelledby="poi-heading"
      >
        <h3 id="poi-heading" class="group-heading">Points of Interest</h3>
        <div role="listbox" aria-labelledby="poi-heading">
          {#each filteredGroupedItems.pois as item, index}
            <button
              class="item-button"
              class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              on:click={(e) => handleItemSelect(item, e)}
              on:focus={() => handleItemFocus(item)}
              aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              role="option"
            >
              {item.title}
            </button>
          {/each}
        </div>
      </div>
    {/if}

    {#if filteredGroupedItems.numericPois.length > 0}
      <div 
        class="group"
        role="region"
        aria-labelledby="numeric-heading"
      >
        <h3 id="numeric-heading" class="group-heading">Numbered Locations</h3>
        <div role="listbox" aria-labelledby="numeric-heading">
          {#each filteredGroupedItems.numericPois as item, index}
            <button
              class="item-button"
              class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              on:click={(e) => handleItemSelect(item, e)}
              on:focus={() => handleItemFocus(item)}
              aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              role="option"
            >
              {item.title}
            </button>
          {/each}
        </div>
      </div>
    {/if}

    {#if filteredGroupedItems.polygonPois.length > 0}
      <div 
        class="group"
        role="region"
        aria-labelledby="polygon-heading"
      >
        <h3 id="polygon-heading" class="group-heading">Areas</h3>
        <div role="listbox" aria-labelledby="polygon-heading">
          {#each filteredGroupedItems.polygonPois as item, index}
            <button
              class="item-button"
              class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              on:click={(e) => handleItemSelect(item, e)}
              on:focus={() => handleItemFocus(item)}
              aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
              role="option"
            >
              {item.title}
            </button>
          {/each}
        </div>
      </div>
    {/if}

    {#if filteredFlattenedItems.length === 0}
      <div class="no-results">
        No locations match your search
      </div>
    {/if}
  </div>
</div>

<style>
  .mapmenu-panel {
    position: absolute;
    top: 90px;
    right: 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 300px;
    max-height: calc(100vh - 40px);
    z-index: 1000;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .header {
    padding: 15px;
    background: #f5f5f5;
    border-bottom: 1px solid #eee;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .header h2 {
    margin: 0;
    font-size: 1.2em;
    color: #333;
  }

  .content {
    overflow-y: auto;
    max-height: 70vh;
  }

  .group {
    border-bottom: 2px solid #eee;
  }

  .group:last-child {
    border-bottom: none;
  }

  .group-heading {
    margin: 0;
    padding: 12px 15px;
    background: #f8f8f8;
    font-size: 1.1em;
    color: #444;
    font-weight: 600;
  }

  .item-button {
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 10px 15px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    color: #333;
    font-size: 14px;
  }

  .item-button:last-child {
    border-bottom: none;
  }

  .item-button:hover {
    background: #f5f5f5;
  }

  .item-button:focus {
    outline: none;
    box-shadow: inset 0 0 0 4px var(--focus-color, #2196f3);
  }

  .item-button.selected {
    background: #e3f2fd;
    box-shadow: inset 0 0 0 2px #2196f3;
  }

  .search {
    padding: 0;
  }

  .search input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
  }

  .search input:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }

  .no-results {
    padding: 20px;
    text-align: center;
    color: #666;
    font-style: italic;
  }
</style>
